import { useMemo } from 'react';
import { useFormState } from 'react-final-form';
import * as yup from 'yup';

import { FieldsListQuery_category_fields as Field } from '../../../../../types/api';

interface GroupRenderProps {
  meta: {
    touched: boolean;
    error: string | undefined;
  };
  validate: { [key: string]: (value: string) => string | undefined };
  required: boolean;
  value: string | boolean | null;
}

export function useGroup(
  groupFields: Field[],
  validationSchema: yup.ObjectSchema,
): GroupRenderProps {
  const { errors, touched, values } = useFormState();

  const groupTouched = useMemo(
    () => groupFields.some(({ id }) => touched && touched[id]),
    [groupFields, touched],
  );

  const groupError = useMemo(() => {
    const groupErrors = groupFields
      .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0))
      .map((gf) => errors[gf.id])
      .filter(Boolean);

    if (groupErrors.length === 0) {
      return undefined;
    }

    return groupErrors.join('|');
  }, [groupFields, errors]);

  const groupValue = useMemo(
    () =>
      groupFields.reduce((value, { id }) => {
        if (values && !!values[id]) {
          return `${value ? value + ' ' : value}${values[id]}`;
        }

        return value;
      }, ''),
    [groupFields, values],
  );

  const groupRequired = useMemo(
    () => groupFields.some(({ required }) => required),
    [groupFields],
  );

  const validators = useMemo(() => {
    return groupFields.reduce((acc, { id }) => {
      acc[id] = (value: string | boolean) => {
        try {
          const trimmedValue = typeof value === 'string' ? value.trim() : value;
          yup.reach(validationSchema, id).validateSync(trimmedValue);
        } catch (error) {
          if (error.name === 'ValidationError') {
            return error.message;
          }
        }
      };

      return acc;
    }, {});
  }, [groupFields]);

  return {
    meta: {
      touched: groupTouched,
      error: groupError,
    },
    validate: validators,
    value: groupValue,
    required: groupRequired,
  };
}
